/**
 * The handoff to production: the email a rep sends Lysa when a job is sold.
 *
 * Laid out as SENDS, the checklist sales and production agreed on 2026-07-28 —
 * Selections, Estimate, Notice to proceed, Deposit, Summary — so that every
 * handoff answers the same five questions in the same order (ADR 0280).
 *
 * The app already knows most of the answers: what was signed, the colours on
 * the lines, which upgrades were offered and which were ticked, the deposit
 * and whether it has arrived, how the customer said they would pay. Those are
 * written in from the job. The rep is asked only what nothing records — when
 * the crew can start, whether a permit is needed, anything still undecided,
 * and a note — so the letter that used to be typed from memory is mostly
 * filled in before they open it.
 *
 * Pure, so the wording is tested and the screen only gathers facts.
 */

import { formatMoney, type Cents } from "./money.js";
import { realColour } from "./selections.js";

/** How the customer said they would pay the deposit. */
export type HandoffPayMethod = "card" | "ach" | "check" | "cash" | "financing";

export const HANDOFF_PAY_METHODS: readonly {
  value: HandoffPayMethod;
  label: string;
}[] = [
  { value: "ach", label: "ACH (bank transfer)" },
  { value: "card", label: "Credit card" },
  { value: "check", label: "Check" },
  { value: "cash", label: "Cash" },
  { value: "financing", label: "Financing" },
];

/** One option that was sold, as the handoff lists it. */
export interface HandoffOption {
  name: string;
  lines: readonly { name: string; color?: string | null }[];
  /** The colour the estimate already gives it, which the question starts on. */
  colour?: string | null;
  /** The colours its products come in, for the question's dropdown. */
  colourOptions?: readonly string[];
  /**
   * Whether one of its products must have its colour chosen: the roof. Its
   * lines are written first under Selections, so the Overview reads the roof's
   * colour back out of the email rather than the gutters' (ADR 0304).
   */
  roof?: boolean;
}

/** What the job already knows, gathered by the screen. */
export interface HandoffFacts {
  repName: string;
  senderName: string;
  customerName: string;
  phone: string;
  address: string;
  jobNumber: string | number;
  bidType: string | null;
  /** What was sold: each option, with its priced lines in order. */
  options: readonly HandoffOption[];
  /**
   * The colours the job's roof products come in, for the one colour question
   * asked when nothing was signed in the app and there is no estimate to ask
   * it of.
   */
  colourOptions?: readonly string[];
  /** Upgrades on the signed document, chosen or included. */
  upgradesChosen: readonly string[];
  /** Upgrades the signed document offered that nobody ticked. */
  upgradesNotChosen: readonly string[];
  /** What the customer agreed to pay. Null when no price can be worked out. */
  contract: Cents | null;
  /** The date it was signed, already written for a person. Null if on paper. */
  signedOn: string | null;
  /** The crew's start date, when a work order is already booked. */
  scheduledStart: string | null;
  /** The first payment on the schedule, and whether it has reached the bank. */
  deposit: { amount: Cents; paid: boolean } | null;
  /** How the customer told the deposit link they would pay, where they have. */
  payMethod: HandoffPayMethod | null;
  /**
   * A rep has said a check is coming and the reminders are still waiting for
   * it (`checkStillExpected`, ADR 0263). A wait that has run out is false:
   * the reminders are going again.
   */
  checkExpected: boolean;
  /** The job is financed (the financing checklist opens at handoff). */
  financing: boolean;
  /** What the customer wrote in the note box when they signed. */
  customerNotes: readonly string[];
}

/** The few things only the rep knows. */
export interface HandoffAnswers {
  /** "Fri 10/16", "end of next week" — whatever the rep told the customer. */
  start: string;
  permit: "yes" | "no" | "unsure" | "";
  /** Null is "not decided yet". */
  payMethod: HandoffPayMethod | null;
  /** Still being decided, one per line. */
  undecided: string;
  /** Anything else production should know. */
  notes: string;
  /**
   * The colour the customer chose for each sold option, in the order of
   * `HandoffFacts.options`; with nothing signed, the one colour for the job.
   * Blank is "not chosen".
   */
  colours?: readonly string[];
}

/**
 * What the questions start out saying, from what the job already records.
 *
 * The upgrades nobody ticked are put in "still deciding" because that is the
 * question production has to ask before the order goes in. A rep whose
 * customer said no deletes the line.
 *
 * How they're paying starts on the deposit link's choice, else Check when a
 * check is expected, else Financing on a financed job, else not decided.
 */
export function handoffDefaults(facts: HandoffFacts): HandoffAnswers {
  return {
    start: facts.scheduledStart ?? "",
    permit: "",
    payMethod:
      facts.payMethod ??
      (facts.checkExpected ? "check" : facts.financing ? "financing" : null),
    undecided: facts.upgradesNotChosen.join("\n"),
    notes: "",
    colours: facts.options.length
      ? facts.options.map((option) => option.colour ?? "")
      : [""],
  };
}

/**
 * Whether the handoff asks the rep to hold the payment reminders for a check
 * (ADR 0301).
 *
 * Asked when the rep says the deposit is coming by check and nothing on the
 * job says so yet — otherwise the customer who has just told their rep "I'll
 * mail it" is reminded two days later (ADR 0263). Not asked when the wait is
 * already on, or when the deposit has already arrived and there is nothing
 * for a reminder to chase.
 */
export function handoffAsksAboutCheckReminders(
  facts: Pick<HandoffFacts, "checkExpected" | "deposit">,
  payMethod: HandoffPayMethod | null,
): boolean {
  return payMethod === "check" && !facts.checkExpected && !facts.deposit?.paid;
}

const PERMIT: Record<Exclude<HandoffAnswers["permit"], "">, string> = {
  yes: "Needed",
  no: "Not needed",
  unsure: "Not sure — please check",
};

function capitalise(value: string | null | undefined): string {
  const text = (value ?? "").trim();
  return text.charAt(0).toUpperCase() + text.slice(1);
}

function lines(text: string): string[] {
  return text
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean);
}

function payLabel(method: HandoffPayMethod | null): string {
  return (
    HANDOFF_PAY_METHODS.find((option) => option.value === method)?.label ??
    "Not decided yet"
  );
}

/**
 * An option's coloured lines, with the colour the rep says the customer chose
 * in place of the one the estimate gave the roof. Every line that carried the
 * estimate's colour follows it — the hip and ridge is the shingle's colour —
 * and one the estimate never coloured is named after the option.
 */
function optionColours(option: HandoffOption, chosen: string): string[] {
  // Given the products, the option's colour is the roof's, and none means the
  // roof was never coloured: a brown drip edge does not stand in for it.
  // Without them, the first real colour does.
  const roof =
    option.colour === undefined
      ? option.lines.map(realColour).find(Boolean)
      : option.colour;
  const was = (roof ?? "").trim().toLowerCase();
  // The lines in the roof's colour go first, so the first colour the Overview
  // reads is the roof's, not a drip edge listed above the shingle.
  const inRoofColour = (line: { color?: string | null }) =>
    was !== "" && String(line.color).trim().toLowerCase() === was;
  const listed = option.lines
    .filter((line) => (line.color ?? "").trim() !== "")
    .sort((a, b) => Number(inRoofColour(b)) - Number(inRoofColour(a)))
    .map((line) => {
      const colour = String(line.color).trim();
      const follows = chosen && was && colour.toLowerCase() === was;
      return `• ${line.name} — ${follows ? chosen : colour}`;
    });
  return chosen && !was ? [`• ${option.name} — ${chosen}`, ...listed] : listed;
}

/** S — what the customer picked. */
function selections(facts: HandoffFacts, answers: HandoffAnswers): string[] {
  const chosen = (index: number) => (answers.colours?.[index] ?? "").trim();
  // The roof's estimate first, whatever order the estimates were signed in:
  // the Overview takes the first colour here as the roof's (ADR 0304).
  const indexed = facts.options.map((option, index) => ({ option, index }));
  const colours = [
    ...indexed.filter(({ option }) => option.roof),
    ...indexed.filter(({ option }) => !option.roof),
  ].flatMap(({ option, index }) => optionColours(option, chosen(index)));
  const upgrades = facts.upgradesChosen.join(", ") || "None";
  const undecided = lines(answers.undecided);
  // With nothing signed there is no option to colour, so the job's one colour
  // is written above what the rep still has to type.
  const unsigned = facts.options.length === 0 && chosen(0);
  const noColours =
    facts.signedOn === null
      ? unsigned
        ? [
            `• Color: ${chosen(0)}`,
            "• Not signed in the app — type the rest of the selections here",
          ]
        : ["• Not signed in the app — type the selections here"]
      : unsigned
        ? [`• Color: ${chosen(0)}`]
        : ["• No colours on the estimate"];
  return [
    "SELECTIONS",
    ...(colours.length ? colours : noColours),
    `• Upgrades: ${upgrades}`,
    ...(undecided.length ? [`• Still deciding: ${undecided.join(", ")}`] : []),
  ];
}

/**
 * E — what was sold. With no signature in the app the rep types it: only
 * signed work is filled in (Jonathon, 2026-10-11, ADR 0280).
 */
function estimate(facts: HandoffFacts): string[] {
  const options = facts.options.flatMap((option) => [
    `• ${option.name}`,
    ...option.lines
      .filter((line) => line.name !== option.name)
      .map((line) => `   – ${line.name}`),
  ]);
  const none =
    facts.signedOn === null
      ? "• Not signed in the app — type what was sold and the contract total here"
      : "• No active estimate on the job";
  return [
    "ESTIMATE",
    ...(options.length ? options : [none]),
    ...(facts.contract === null
      ? []
      : [`• Contract: ${formatMoney(facts.contract)}`]),
  ];
}

/** N — the go-ahead. */
function noticeToProceed(
  facts: HandoffFacts,
  answers: HandoffAnswers,
): string[] {
  return [
    "NOTICE TO PROCEED",
    `• Signed: ${facts.signedOn ?? "On paper"}`,
    `• Start: ${answers.start.trim() || "Not set yet"}`,
    ...(answers.permit ? [`• Permit: ${PERMIT[answers.permit]}`] : []),
  ];
}

/** D — the money. */
function deposit(facts: HandoffFacts, answers: HandoffAnswers): string[] {
  const { deposit: due } = facts;
  const financed = facts.financing || answers.payMethod === "financing";
  return [
    "DEPOSIT",
    ...(due
      ? [
          `• Deposit: ${formatMoney(due.amount)} — ${due.paid ? "received" : "not received yet"}`,
        ]
      : []),
    `• Paying by: ${payLabel(answers.payMethod)}`,
    ...(financed ? ["• Financed: Yes"] : []),
  ];
}

/** S — everything else. Left out when there is nothing to say. */
function summary(facts: HandoffFacts, answers: HandoffAnswers): string[] {
  const notes = [
    ...lines(answers.notes),
    ...facts.customerNotes
      .map((note) => note.trim())
      .filter(Boolean)
      .map((note) => `Customer's note when signing: "${note}"`),
  ];
  return notes.length ? ["SUMMARY", ...notes] : [];
}

/** The subject and body of the handoff email. */
export function handoffEmail(
  facts: HandoffFacts,
  answers: HandoffAnswers,
): { subject: string; body: string } {
  const bidType = capitalise(facts.bidType);
  const intro = [
    "Hey Lysa,",
    "",
    `${facts.repName} just closed a new ${bidType ? `${bidType} ` : ""}job — ready to hand off to production.`,
    "",
    `Customer: ${facts.customerName}`,
    `Job address: ${facts.address}`,
    `Phone: ${facts.phone}`,
    `Job #: ${facts.jobNumber}`,
  ];
  const sections = [
    selections(facts, answers),
    estimate(facts),
    noticeToProceed(facts, answers),
    deposit(facts, answers),
    summary(facts, answers),
  ].filter((section) => section.length > 0);

  return {
    subject: `New signed job — ${facts.address} (#${facts.jobNumber})`,
    body: [
      ...intro,
      ...sections.flatMap((section) => ["", ...section]),
      "",
      "Thanks,",
      facts.senderName,
    ].join("\n"),
  };
}
